import { placeMarkupTextLayers } from '../MapBoxViewer/mapboxLayerOrder'
import {
  markupTextLayerIds,
  registerMarkupTextLayer,
} from '../MapBoxViewer/markupTextLayerIds'
import { mercatorX, mercatorY } from '../MapBoxViewer/rasterTileMath'
import { textBandFor, textGroundPerPx } from './markupTextScale'
import { ITextLayout } from './textMarkupLayout'
import { textMarkupEntry } from './textMarkupRegistry'
import {
  ITextTexture,
  TextMarkupTextures,
  textMarkupTextures,
} from './textMarkupTextures'

export interface ITextMarkupItem {
  id: string
  specId: string
  lng: number
  lat: number
  // Degrees clockwise from north, in the map plane.
  rotation: number
}

export interface ITextMarkupLayerSnapshot {
  id: string
  items: ITextMarkupItem[]
  opacity: number
  sizeScale: number
}

// An item's values that only change with setItems, so a frame does not recompute them.
interface IPlacedItem {
  item: ITextMarkupItem
  mx: number
  my: number
  cos: number
  sin: number
  layout: ITextLayout | undefined
}

interface IQuad {
  // Anchor and half extents in mercator units; cos/sin of the rotation.
  mx: number
  my: number
  hx: number
  hy: number
  cos: number
  sin: number
  texture: ITextTexture
}

const VERTEX = `
attribute vec2 a_pos;
attribute vec2 a_uv;
uniform mat4 u_matrix;
varying vec2 v_uv;
void main() {
  v_uv = a_uv;
  gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0);
}`

const FRAGMENT = `
precision mediump float;
uniform sampler2D u_texture;
uniform float u_opacity;
varying vec2 v_uv;
void main() {
  gl_FragColor = texture2D(u_texture, v_uv) * u_opacity;
}`

// Column-major a × b, in double precision.
function multiply(a: ArrayLike<number>, b: ArrayLike<number>): Float32Array {
  const out = new Float32Array(16)
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      let v = 0
      for (let k = 0; k < 4; k++) v += a[k * 4 + r] * b[c * 4 + k]
      out[c * 4 + r] = v
    }
  }
  return out
}

// Tile-local [0,1]² to the drape framebuffer's clip space (mapbox's drape ortho puts y=0 at
// the bottom, matching tile coordinates).
const TILE_TO_CLIP = new Float32Array([
  2, 0, 0, 0, 0, 2, 0, 0, 0, 0, 1, 0, -1, -1, 0, 1,
])

// Mapbox caches drape tiles; they are redrawn only while this many frames are pending.
const REDRAPE_FRAMES = 2

// Live layers per map, for hit-testing: queryRenderedFeatures cannot see custom layers.
const layersByMap = new WeakMap<mapboxgl.Map, Map<string, TextMarkupGLLayer>>()

// Markup ids of the texts under a canvas point, topmost first, optionally only from the
// given layers.
export function queryTextMarkups(
  map: mapboxgl.Map,
  point: { x: number; y: number },
  layerIds?: string[],
): string[] {
  const live = layersByMap.get(map)
  if (!live) return []
  return [...markupTextLayerIds(map)]
    .reverse()
    .filter(id => !layerIds || layerIds.includes(id))
    .flatMap(id => live.get(id)?.hit(point) ?? [])
}

// The map's text layers with something to draw, in mount order: a map built from this one's
// getStyle() never gets them, since custom layers are not part of a style.
export function snapshotTextMarkupLayers(
  map: mapboxgl.Map,
): ITextMarkupLayerSnapshot[] {
  const live = layersByMap.get(map)
  return markupTextLayerIds(map).flatMap(id => {
    const snapshot = live?.get(id)?.snapshot()
    return snapshot?.items.length ? [snapshot] : []
  })
}

// Recreates snapshotted text layers on a map whose style has loaded, placed as on the source map.
export function addTextMarkupLayers(
  map: mapboxgl.Map,
  snapshots: ITextMarkupLayerSnapshot[],
): void {
  for (const { id, items, opacity, sizeScale } of snapshots) {
    const layer = new TextMarkupGLLayer(id)
    registerMarkupTextLayer(map, id)
    map.addLayer(layer)
    layer.setItems(items, opacity, null, sizeScale)
  }
  placeMarkupTextLayers(map)
}

/**
 * Saved text markups as one textured quad each on the ground plane: sized by the text scale
 * like the DOM box, in true perspective, and independent of map tiles (no anchor-tile pop).
 * Flat maps draw in `render`; terrain and globe drape it through `renderToTile`.
 */
export class TextMarkupGLLayer {
  public readonly type = 'custom' as const
  public readonly renderingMode = '2d' as const
  private map: mapboxgl.Map | null = null
  private gl: WebGL2RenderingContext | null = null
  private textures: TextMarkupTextures | null = null
  private program: WebGLProgram | null = null
  private buffer: WebGLBuffer | null = null
  private locations: {
    pos: number
    uv: number
    matrix: WebGLUniformLocation
    texture: WebGLUniformLocation
    opacity: WebGLUniformLocation
  } | null = null
  private placed: IPlacedItem[] = []
  private opacity = 1
  private highlightId: string | null = null
  // Every text drawn this many times its size, each around its own anchor.
  private sizeScale = 1
  private quads: IQuad[] = []
  private band = -1
  private groundScale = -1
  private redrapeFrames = REDRAPE_FRAMES
  private readonly vertices = new Float32Array(16)

  public constructor(public readonly id: string) {}

  public setItems(
    items: ITextMarkupItem[],
    opacity: number,
    highlightId: string | null = null,
    sizeScale = 1,
  ): void {
    this.placed = items.map(item => {
      const r = (item.rotation * Math.PI) / 180
      return {
        item,
        mx: mercatorX(item.lng),
        my: mercatorY(item.lat),
        cos: Math.cos(r),
        sin: Math.sin(r),
        layout: textMarkupEntry(item.specId)?.layout,
      }
    })
    this.opacity = opacity
    this.highlightId = highlightId
    this.sizeScale = sizeScale
    this.redrapeFrames = REDRAPE_FRAMES
    this.map?.triggerRepaint()
  }

  public snapshot(): ITextMarkupLayerSnapshot {
    return {
      id: this.id,
      items: this.placed.map(placed => placed.item),
      opacity: this.opacity,
      sizeScale: this.sizeScale,
    }
  }

  public onAdd(map: mapboxgl.Map, gl: WebGL2RenderingContext): void {
    // A style reload re-adds the layer without calling onRemove: the old program goes first.
    if (this.map === map) this.releaseGl()
    this.map = map
    this.gl = gl
    if (!layersByMap.has(map)) layersByMap.set(map, new Map())
    layersByMap.get(map).set(this.id, this)
    this.textures = textMarkupTextures(map, gl)
    const program = gl.createProgram()
    const shader = (type: number, source: string): WebGLShader => {
      const s = gl.createShader(type)
      gl.shaderSource(s, source)
      gl.compileShader(s)
      return s
    }
    const shaders = [
      shader(gl.VERTEX_SHADER, VERTEX),
      shader(gl.FRAGMENT_SHADER, FRAGMENT),
    ]
    shaders.forEach(s => gl.attachShader(program, s))
    gl.linkProgram(program)
    shaders.forEach(s => {
      gl.detachShader(program, s)
      gl.deleteShader(s)
    })
    this.program = program
    this.buffer = gl.createBuffer()
    this.locations = {
      pos: gl.getAttribLocation(program, 'a_pos'),
      uv: gl.getAttribLocation(program, 'a_uv'),
      matrix: gl.getUniformLocation(program, 'u_matrix'),
      texture: gl.getUniformLocation(program, 'u_texture'),
      opacity: gl.getUniformLocation(program, 'u_opacity'),
    }
    this.redrapeFrames = REDRAPE_FRAMES
  }

  public onRemove(): void {
    this.detach()
  }

  // Leaves the map: what onRemove does, also for a layer a style reload dropped without calling
  // it. Textures belong to the map's cache and survive; only our program goes.
  public detach(): void {
    const map = this.map
    if (!map) return
    if (layersByMap.get(map)?.get(this.id) === this) {
      layersByMap.get(map).delete(this.id)
    }
    if (!layersByMap.get(map)?.size) this.textures?.dropQueue()
    this.releaseGl()
    this.map = null
    this.gl = null
  }

  private releaseGl(): void {
    if (this.buffer) this.gl.deleteBuffer(this.buffer)
    if (this.program) this.gl.deleteProgram(this.program)
    this.program = null
    this.buffer = null
  }

  public shouldRerenderTiles(): boolean {
    return this.redrapeFrames > 0
  }

  // Once per frame, outside any framebuffer: cull, pick textures (a text never painted is
  // painted here), and paint queued bands within the budget.
  public prerender(): void {
    const map = this.map
    if (!map) return
    if (this.redrapeFrames > 0) this.redrapeFrames--
    const zoom = map.getZoom()
    const band = textBandFor(zoom, this.sizeScale)
    const groundScale = textGroundPerPx(zoom) * this.sizeScale
    if (
      band !== this.band ||
      Math.abs(groundScale / this.groundScale - 1) > 1e-9
    ) {
      this.band = band
      this.groundScale = groundScale
      this.redrapeFrames = REDRAPE_FRAMES
    }
    if (!this.placed.length) {
      this.quads = []
      return
    }
    const bounds = map.getBounds()
    const west = mercatorX(bounds.getWest())
    const east = mercatorX(bounds.getEast())
    const north = mercatorY(bounds.getNorth())
    const south = mercatorY(bounds.getSouth())
    const quads: IQuad[] = []
    for (const { item, mx, my, cos, sin, layout } of this.placed) {
      if (!layout) continue
      const reach = (Math.hypot(layout.width, layout.height) / 2) * groundScale
      if (
        mx + reach < west ||
        mx - reach > east ||
        my + reach < north ||
        my - reach > south
      ) {
        continue
      }
      const texture = this.textures.textureFor(item.specId, band)
      if (!texture) continue
      const quad = (t: ITextTexture): IQuad => ({
        mx,
        my,
        hx: (t.width / 2) * groundScale,
        hy: (t.height / 2) * groundScale,
        cos,
        sin,
        texture: t,
      })
      const ring =
        item.id === this.highlightId &&
        this.textures.textureFor(item.specId, band, true)
      if (ring) quads.push(quad(ring))
      quads.push(quad(texture))
    }
    this.quads = quads
    if (this.textures.drain(band)) this.redrapeFrames = REDRAPE_FRAMES
    if (this.textures.hasQueued || this.redrapeFrames > 0) map.triggerRepaint()
  }

  // Ground-plane hit: the point unprojected onto the map, tested inside each rotated box, so
  // it matches the drawn quad under pitch and on terrain. Topmost (last drawn) first.
  public hit(point: { x: number; y: number }): string[] {
    const map = this.map
    if (!map) return []
    const at = map.unproject([point.x, point.y])
    const groundScale = textGroundPerPx(map.getZoom()) * this.sizeScale
    const px = mercatorX(at.lng)
    const py = mercatorY(at.lat)
    const hits: string[] = []
    for (let i = this.placed.length - 1; i >= 0; i--) {
      const { item, mx, my, cos, sin, layout } = this.placed[i]
      if (!layout) continue
      const dx = (px - mx) / groundScale
      const dy = (py - my) / groundScale
      // Turned back by the rotation: the inverse of the quad's turn.
      const x = dx * cos + dy * sin
      const y = -dx * sin + dy * cos
      if (Math.abs(x) <= layout.width / 2 && Math.abs(y) <= layout.height / 2) {
        hits.push(item.id)
      }
    }
    return hits
  }

  public render(gl: WebGL2RenderingContext, matrix: number[]): void {
    const map = this.map
    if (!map || !this.program || !this.quads.length) return
    const worldSize = 512 * 2 ** map.getZoom()
    const center = map.getCenter()
    const ox = mercatorX(center.lng)
    const oy = mercatorY(center.lat)
    // Vertices are offsets from the camera centre in screen-scale units, float32-safe at
    // max zoom; the translation and 1/worldSize ride in the matrix, built in doubles.
    this.begin(
      gl,
      multiply(matrix, [
        1 / worldSize,
        0,
        0,
        0,
        0,
        1 / worldSize,
        0,
        0,
        0,
        0,
        1,
        0,
        ox,
        oy,
        0,
        1,
      ]),
    )
    for (const quad of this.quads) {
      this.draw(
        gl,
        quad,
        (quad.mx - ox) * worldSize,
        (quad.my - oy) * worldSize,
        worldSize,
      )
    }
    this.end(gl)
  }

  public renderToTile(
    gl: WebGL2RenderingContext,
    tile: { x: number; y: number; z: number },
  ): void {
    if (!this.program || !this.quads.length) return
    const n = 2 ** tile.z
    // The tile id carries the world copy in x; quads live in the primary copy.
    const x = ((tile.x % n) + n) % n
    this.begin(gl, TILE_TO_CLIP)
    for (const quad of this.quads) {
      const ax = quad.mx * n - x
      const ay = quad.my * n - tile.y
      const reach = Math.hypot(quad.hx, quad.hy) * n
      if (
        ax + reach < 0 ||
        ax - reach > 1 ||
        ay + reach < 0 ||
        ay - reach > 1
      ) {
        continue
      }
      this.draw(gl, quad, ax, ay, n)
    }
    this.end(gl)
  }

  // Mapbox marks its own GL state dirty after a custom layer; we set everything we use.
  private begin(gl: WebGL2RenderingContext, matrix: Float32Array): void {
    const at = this.locations
    gl.bindVertexArray(null)
    gl.useProgram(this.program)
    gl.uniformMatrix4fv(at.matrix, false, matrix)
    gl.uniform1i(at.texture, 0)
    gl.uniform1f(at.opacity, this.opacity)
    gl.activeTexture(gl.TEXTURE0)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
    gl.disable(gl.DEPTH_TEST)
    gl.disable(gl.STENCIL_TEST)
    gl.disable(gl.CULL_FACE)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer)
    gl.enableVertexAttribArray(at.pos)
    gl.enableVertexAttribArray(at.uv)
    gl.vertexAttribPointer(at.pos, 2, gl.FLOAT, false, 16, 0)
    gl.vertexAttribPointer(at.uv, 2, gl.FLOAT, false, 16, 8)
  }

  private end(gl: WebGL2RenderingContext): void {
    gl.disableVertexAttribArray(this.locations.pos)
    gl.disableVertexAttribArray(this.locations.uv)
  }

  // (ox, oy): the anchor in the target space; scale: target units per mercator unit.
  private draw(
    gl: WebGL2RenderingContext,
    quad: IQuad,
    ox: number,
    oy: number,
    scale: number,
  ): void {
    const v = this.vertices
    const hx = quad.hx * scale
    const hy = quad.hy * scale
    const corner = (
      x: number,
      y: number,
      u: number,
      w: number,
      o: number,
    ): void => {
      v[o] = ox + x * quad.cos - y * quad.sin
      v[o + 1] = oy + x * quad.sin + y * quad.cos
      v[o + 2] = u
      v[o + 3] = w
    }
    corner(-hx, -hy, 0, 0, 0)
    corner(hx, -hy, 1, 0, 4)
    corner(-hx, hy, 0, 1, 8)
    corner(hx, hy, 1, 1, 12)
    gl.bufferData(gl.ARRAY_BUFFER, v, gl.STREAM_DRAW)
    gl.bindTexture(gl.TEXTURE_2D, quad.texture.texture)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
  }
}
